import { RulesSatisfactionType, RuleType } from './types';
import { checkIfRulesSatisfied } from '../helpers';
import { useFormikContext } from 'formik';

const rulesByType = (rules: Array<RuleType>, type: RuleType['type']) =>
  rules.filter(rule => rule.type === type);

const evaluateWithRules = (
  formValues: any,
  rules: Array<RuleType>,
  rulesSatisfactionType: RulesSatisfactionType,
  fallback: boolean
): boolean =>
  rules.length > 0
    ? checkIfRulesSatisfied(formValues, rules, rulesSatisfactionType)
    : fallback;

const applyOptionsVisibility = (
  fieldOptions: Array<any>,
  optionsVisibilityRules: Array<RuleType>,
  formValues: any,
  rulesSatisfactionType: RulesSatisfactionType
): Array<any> =>
  fieldOptions.filter(option => {
    const compositeKey = `${option.displayLabel?.en} - ${option.optionValue}`;
    const targetingRules = optionsVisibilityRules.filter(rule => rule.options?.includes(compositeKey));
    if (targetingRules.length === 0) return true;
    return checkIfRulesSatisfied(formValues, targetingRules, rulesSatisfactionType);
  });

export const RuleEvaluatorWrapper = ({
  rules = [],
  rulesSatisfactionType = 'all',
  children,
  elementType,
}: {
    rules?: Array<RuleType>;
  rulesSatisfactionType?: RulesSatisfactionType;
  children: React.ReactElement;
  elementType: 'field' | 'section' | 'page';
}) => {
  const formik: any = useFormikContext();

  const itemConfig = elementType === 'field'
    ? children.props.itemData.config
    : children.props.config.config;

  const defaultRules = rules.filter(rule => !rule.type || rule.type === 'default');
  itemConfig.displayed = evaluateWithRules(
    formik.values,
    defaultRules,
    rulesSatisfactionType,
    elementType === 'field' ? itemConfig.displayed : true
  );

  if (elementType === 'field') {
    const requiredRules = rulesByType(rules, 'required');
    itemConfig.required = evaluateWithRules(
      formik.values,
      requiredRules,
      rulesSatisfactionType,
      itemConfig.required
    );

    const optionsVisibilityRules = rulesByType(rules, 'optionsVisibility');
    if (itemConfig.fieldOptions?.length > 0 && optionsVisibilityRules.length > 0) {
      itemConfig.fieldOptions = applyOptionsVisibility(
        itemConfig.fieldOptions,
        optionsVisibilityRules,
        formik.values,
        rulesSatisfactionType
      );
    }
  }

  return { ...children, props: { ...children.props } };
};
